/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../components/ui/modal.js';
import { type Session } from '../../lib/api/sessions.js';
import { useRooms } from '../../lib/queries/rooms.js';
import { formatIst, istDayKey } from '../../lib/utils/datetime.js';
import { SessionDetails } from '../therapist/session-table.js';

const START_HOUR = 8;
const END_HOUR = 19;
const HOUR_PX = 56;
const HOURS = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** Distinct block colours (Figma uses a per-session palette). */
const COLORS = [
  'border-l-rose-400 bg-rose-50 text-rose-900 dark:bg-rose-500/10 dark:text-rose-200',
  'border-l-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200',
  'border-l-sky-400 bg-sky-50 text-sky-900 dark:bg-sky-500/10 dark:text-sky-200',
  'border-l-violet-400 bg-violet-50 text-violet-900 dark:bg-violet-500/10 dark:text-violet-200',
  'border-l-emerald-400 bg-emerald-50 text-emerald-900 dark:bg-emerald-500/10 dark:text-emerald-200',
  'border-l-fuchsia-400 bg-fuchsia-50 text-fuchsia-900 dark:bg-fuchsia-500/10 dark:text-fuchsia-200',
] as const;

function pad(n: number): string {
  return n < 10 ? `0${String(n)}` : String(n);
}
function dayKeyOf(d: Date): string {
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function startOfWeek(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); // back to Monday
  return x;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
/** IST hour-of-day as a float (e.g. 10:30 → 10.5). */
function startFloat(iso: string): number {
  const [h, m] = formatIst(iso, 'HH:mm').split(':');
  return Number(h) + Number(m) / 60;
}
/** Plain 12-hour clock label for the gutter (no timezone shift). */
function hourLabel(h: number): string {
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${String(hr)} ${h < 12 ? 'AM' : 'PM'}`;
}
function colorFor(key: string): string {
  let h = 0;
  for (const ch of key) {
    h = (h * 31 + ch.charCodeAt(0)) % COLORS.length;
  }
  return COLORS[h] ?? COLORS[0];
}

function SessionBlock({
  session,
  clientNames,
  onSelect,
}: {
  readonly session: Session;
  readonly clientNames: ReadonlyMap<string, string>;
  readonly onSelect: (s: Session) => void;
}): JSX.Element {
  const top = (startFloat(session.scheduledStart) - START_HOUR) * HOUR_PX;
  const end = session.scheduledEnd !== null ? startFloat(session.scheduledEnd) : null;
  const height = Math.max(
    end !== null ? (end - startFloat(session.scheduledStart)) * HOUR_PX : HOUR_PX,
    34,
  );
  const select = useCallback(() => {
    onSelect(session);
  }, [onSelect, session]);
  return (
    <button
      type="button"
      onClick={select}
      className={`focus-ring absolute left-1 right-1 overflow-hidden rounded-md border-l-4 px-2 py-1 text-left text-[11px] ${colorFor(session.therapistId ?? session.sessionType ?? session.id)}`}
      style={{ top: `${String(Math.max(top, 0))}px`, height: `${String(height)}px` }}
    >
      <p className="truncate font-semibold">{clientNames.get(session.clientId) ?? '—'}</p>
      {session.sessionType !== null && <p className="truncate opacity-80">{session.sessionType}</p>}
      <p className="truncate opacity-70">{formatIst(session.scheduledStart, 'h:mm a')}</p>
    </button>
  );
}

function DayColumn({
  dayDate,
  sessions,
  clientNames,
  onSelect,
}: {
  readonly dayDate: Date;
  readonly sessions: readonly Session[];
  readonly clientNames: ReadonlyMap<string, string>;
  readonly onSelect: (s: Session) => void;
}): JSX.Element {
  const key = dayKeyOf(dayDate);
  const day = sessions.filter((s) => istDayKey(s.scheduledStart) === key);
  return (
    <div
      className="relative border-l border-slate-100 dark:border-slate-800"
      style={{ height: `${String(HOURS.length * HOUR_PX)}px` }}
    >
      {HOURS.map((h) => (
        <div
          key={h}
          className="border-b border-slate-50 dark:border-slate-800/60"
          style={{ height: `${String(HOUR_PX)}px` }}
        />
      ))}
      {day.map((s) => (
        <SessionBlock key={s.id} session={s} clientNames={clientNames} onSelect={onSelect} />
      ))}
    </div>
  );
}

/** Calendar header: range label + prev/today/next navigation. */
function CalendarHeader({
  rangeLabel,
  onPrev,
  onToday,
  onNext,
}: {
  readonly rangeLabel: string;
  readonly onPrev: () => void;
  readonly onToday: () => void;
  readonly onNext: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between border-b border-slate-100 p-3 dark:border-slate-800">
      <h3 className="font-semibold text-slate-800 dark:text-white">{rangeLabel}</h3>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPrev}
          aria-label={t('common.previous')}
          className="focus-ring rounded-lg p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onToday}
          className="focus-ring rounded-lg px-3 py-1 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          {t('admin.sessions.today')}
        </button>
        <button
          type="button"
          onClick={onNext}
          aria-label={t('common.next')}
          className="focus-ring rounded-lg p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/** Week/Day time-grid calendar with colored session blocks (Figma Schedule). */
export function ScheduleCalendar({
  sessions,
  clientNames,
  mode,
}: {
  readonly sessions: readonly Session[];
  readonly clientNames: ReadonlyMap<string, string>;
  readonly mode: 'week' | 'day';
}): JSX.Element {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [details, setDetails] = useState<Session | null>(null);
  const rooms = useRooms();
  const roomNames = useMemo(
    () => new Map((rooms.data ?? []).map((r) => [r.id, r.name])),
    [rooms.data],
  );
  const onSelect = useCallback((s: Session) => {
    setDetails(s);
  }, []);
  const onDetailsChange = useCallback((open: boolean) => {
    if (!open) {
      setDetails(null);
    }
  }, []);

  const days = useMemo(() => {
    if (mode === 'day') {
      return [anchor];
    }
    const mon = startOfWeek(anchor);
    return Array.from({ length: 7 }, (_, i) => addDays(mon, i));
  }, [anchor, mode]);

  const rangeLabel = useMemo(() => {
    if (mode === 'day') {
      return formatIst(anchor.toISOString(), 'EEE, MMM d');
    }
    const first = days[0];
    const last = days[6];
    return first !== undefined && last !== undefined
      ? `${formatIst(first.toISOString(), 'MMM d')} – ${formatIst(last.toISOString(), 'MMM d')}`
      : '';
  }, [days, anchor, mode]);

  const step = mode === 'day' ? 1 : 7;
  const prev = useCallback(() => {
    setAnchor((c) => addDays(c, -step));
  }, [step]);
  const next = useCallback(() => {
    setAnchor((c) => addDays(c, step));
  }, [step]);
  const today = useCallback(() => {
    setAnchor(new Date());
  }, []);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <CalendarHeader rangeLabel={rangeLabel} onPrev={prev} onToday={today} onNext={next} />
      <div className="overflow-x-auto">
        <div className="flex min-w-[640px]">
          <div className="w-14 shrink-0">
            <div className="h-9" />
            {HOURS.map((h) => (
              <div
                key={h}
                className="pr-2 text-right text-[11px] text-slate-400"
                style={{ height: `${String(HOUR_PX)}px` }}
              >
                {hourLabel(h)}
              </div>
            ))}
          </div>
          <div
            className="grid flex-1"
            style={{ gridTemplateColumns: `repeat(${String(days.length)}, minmax(0, 1fr))` }}
          >
            {days.map((d) => (
              <div key={dayKeyOf(d)}>
                <div className="flex h-9 items-center justify-center gap-1 border-b border-slate-100 text-xs font-medium text-slate-500 dark:border-slate-800">
                  {mode === 'week' && <span>{WEEKDAYS[(d.getDay() + 6) % 7]}</span>}
                  <span className="font-semibold text-slate-700 dark:text-slate-200">
                    {formatIst(d.toISOString(), mode === 'day' ? 'EEE d' : 'd')}
                  </span>
                </div>
                <DayColumn
                  dayDate={d}
                  sessions={sessions}
                  clientNames={clientNames}
                  onSelect={onSelect}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <Modal
        open={details !== null}
        onOpenChange={onDetailsChange}
        title={t('therapist.sessions.detailsTitle')}
        description={details !== null ? (clientNames.get(details.clientId) ?? '') : undefined}
        closeLabel={t('common.cancel')}
      >
        {details !== null && (
          <SessionDetails
            session={details}
            clientName={clientNames.get(details.clientId) ?? '—'}
            roomName={details.roomId !== null ? (roomNames.get(details.roomId) ?? '—') : '—'}
          />
        )}
      </Modal>
    </div>
  );
}
